import Alpine from 'alpinejs';
import { createAuthStore } from './stores/auth.js';
import { createI18nStore } from './i18n/index.js';
import { createRouter } from './router/index.js';
import { registerSW } from 'virtual:pwa-register';
import { toJalali, toGregorian } from './utils/jalali.js';
import { navBarComponent } from './components/nav-bar.js';
import { tagInput, tagFilterBar } from './components/tag-input.js';
import { datePicker } from './components/date-picker.js';
import { searchApi } from './api/search.js';
import { nlSearchApi } from './api/nl-search.js';
import { aiApi, attentionApi } from './api/ai.js';
import { requestPushPermission, onForegroundMessage } from './firebase.js';
import { fcmApi } from './api/fcm.js';

window.Alpine = Alpine;

window.toJalali = (date, format) => {
  const store = window.Alpine?.store?.('i18n');
  return store?.resolvedCalendar() === 'jalali' ? toJalali(date, format) : toGregorian(date, format);
};

Alpine.magic('jalali', () => {
  const store = Alpine.store('i18n');
  // Access reactive properties to track dependencies for re-render
  store.calPref;
  store.locale;
  return (date, format) => {
    return store.resolvedCalendar() === 'jalali' ? toJalali(date, format) : toGregorian(date, format);
  };
});
Alpine.magic('t', () => {
  const store = Alpine.store('i18n');
  store.locale; // track locale so x-text="$t(...)" re-renders on locale change
  return (key, params) => store.t(key, params);
});

Alpine.store('auth', createAuthStore());
Alpine.store('i18n', createI18nStore());

// Proactive token refresh: if access token is expired on page load, refresh before first API call
(async () => {
  const auth = Alpine.store('auth');
  if (!auth.accessToken || !auth.refreshToken) return;
  try {
    const [, payload] = auth.accessToken.split('.');
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    if (exp && exp - Date.now() / 1000 < 60) {
      const { default: axios } = await import('axios');
      const { data } = await axios.post('/api/v1/auth/refresh', { refresh_token: auth.refreshToken });
      auth.setTokens(data.data.access_token, data.data.refresh_token);
    }
  } catch { /* ignore — interceptor handles it on first real call */ }
})();

// Firebase push: register token once user is authenticated
async function initPush() {
  const auth = Alpine.store('auth');
  if (!auth.isAuthenticated) return;
  const stored = localStorage.getItem('chehrecrm_fcm_token');
  const token  = await requestPushPermission();
  if (token && token !== stored) {
    try {
      await fcmApi.register(token);
      localStorage.setItem('chehrecrm_fcm_token', token);
    } catch {}
  }
}

// Show foreground push as a toast banner
function showPushToast(title, body) {
  const el = document.createElement('div');
  el.style.cssText = [
    'position:fixed', 'top:1rem', 'right:1rem', 'max-width:320px',
    'background:#1e3a5f', 'color:#fff', 'padding:0.75rem 1rem',
    'border-radius:0.75rem', 'font-size:0.85rem', 'z-index:9999',
    'box-shadow:0 4px 16px rgba(0,0,0,.35)', 'cursor:pointer',
    'display:flex', 'flex-direction:column', 'gap:0.25rem',
  ].join(';');
  el.innerHTML = `<strong style="font-size:0.9rem">${title}</strong><span style="opacity:.85">${body}</span>`;
  el.onclick = () => el.remove();
  document.body.appendChild(el);
  setTimeout(() => el?.remove(), 6000);
}

onForegroundMessage((payload) => {
  const t = payload.notification?.title ?? 'ChehreCRM';
  const b = payload.notification?.body  ?? '';
  showPushToast(t, b);

  // Bump unread badge if notification bell is loaded
  try {
    const nav = document.querySelector('[x-data]')?._x_dataStack?.[0];
    if (nav?.loadNotifications) nav.loadNotifications();
  } catch {}
});

Alpine.data('navBar', navBarComponent);
Alpine.data('tagInput', (config) => tagInput(config));
Alpine.data('tagFilter', (config) => tagFilterBar(config));
Alpine.data('datePicker', (config) => datePicker(config));

Alpine.store('search', {
  open: false,
  query: '',
  results: { contacts: [], companies: [], leads: [] },
  loading: false,
  _timer: null,

  // NL search
  mode: 'keyword',  // 'keyword' | 'nl'
  nlQuery: '',
  nlResults: null,   // { results: {contacts,leads,companies}, interpretation, spec }
  nlLoading: false,
  nlError: null,

  show() {
    this.open = true;
    setTimeout(() => {
      const el = this.mode === 'nl'
        ? document.getElementById('nl-search-input')
        : document.getElementById('global-search-input');
      el?.focus();
    }, 30);
  },

  hide() {
    this.open = false;
    this.query = '';
    this.nlQuery = '';
    this.results = { contacts: [], companies: [], leads: [] };
    this.nlResults = null;
    this.nlError = null;
    this.loading = false;
    this.nlLoading = false;
    clearTimeout(this._timer);
  },

  setMode(m) {
    this.mode = m;
    this.results = { contacts: [], companies: [], leads: [] };
    this.nlResults = null;
    this.nlError = null;
    setTimeout(() => {
      const el = m === 'nl'
        ? document.getElementById('nl-search-input')
        : document.getElementById('global-search-input');
      el?.focus();
    }, 30);
  },

  onInput() {
    clearTimeout(this._timer);
    if (this.query.length < 2) {
      this.results = { contacts: [], companies: [], leads: [] };
      return;
    }
    this._timer = setTimeout(() => this.doSearch(), 300);
  },

  async doSearch() {
    this.loading = true;
    try {
      const { data } = await searchApi.global(this.query);
      this.results = data.data;
    } catch {
      this.results = { contacts: [], companies: [], leads: [] };
    } finally {
      this.loading = false;
    }
  },

  async doNlSearch() {
    if (!this.nlQuery.trim()) return;
    this.nlLoading = true;
    this.nlError = null;
    this.nlResults = null;
    try {
      const { data } = await nlSearchApi.search(this.nlQuery.trim());
      this.nlResults = data.data;
    } catch (e) {
      this.nlError = e?.response?.data?.error ?? 'Search failed';
    } finally {
      this.nlLoading = false;
    }
  },

  navigate(path) {
    window.location.hash = path;
    this.hide();
  },

  get hasResults() {
    return this.results.contacts.length > 0 || this.results.companies.length > 0 || this.results.leads.length > 0;
  },
  get showEmpty() {
    return !this.loading && this.query.length >= 2 && !this.hasResults;
  },
  get showHint() {
    return !this.loading && this.query.length < 2;
  },
  get nlHasResults() {
    if (!this.nlResults) return false;
    const r = this.nlResults.results ?? {};
    return (r.contacts?.length > 0) || (r.leads?.length > 0) || (r.companies?.length > 0);
  },
});

document.addEventListener('keydown', (e) => {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault();
    Alpine.store('search').show();
  } else if ((e.metaKey || e.ctrlKey) && e.key === '/') {
    e.preventDefault();
    Alpine.store('aiCmd').show();
  } else if (e.key === 'Escape') {
    if (Alpine.store('aiCmd')?.open) Alpine.store('aiCmd').hide();
    else if (Alpine.store('search')?.open) Alpine.store('search').hide();
  }
});

Alpine.store('aiCmd', {
  open: false,
  command: '',
  loading: false,
  result: null,
  error: null,
  examples: [
    'Add project "Q3 Launch" with deadline next Friday',
    'Create a task to follow up with clients, urgent',
    'Create a meeting tomorrow at 3pm about the proposal',
    'Add a new lead for Acme Corp, negotiation stage',
    'Create contact Sara Ahmadi, phone 09123456789',
  ],

  show() {
    this.open = true;
    this.result = null;
    this.error = null;
    setTimeout(() => document.getElementById('ai-cmd-input')?.focus(), 30);
  },

  hide() {
    this.open = false;
  },

  reset() {
    this.command = '';
    this.result = null;
    this.error = null;
    setTimeout(() => document.getElementById('ai-cmd-input')?.focus(), 30);
  },

  async run() {
    const cmd = this.command.trim();
    if (!cmd || this.loading) return;
    this.loading = true;
    this.result = null;
    this.error = null;
    try {
      const { data } = await aiApi.command(cmd);
      this.result = data.data;
    } catch (e) {
      this.error = e?.response?.data?.error ?? 'AI unavailable — make sure Ollama is running.';
    } finally {
      this.loading = false;
    }
  },
});

Alpine.store('attention', {
  count: 0,
  async refresh() {
    try {
      const { data } = await attentionApi.get();
      this.count = data.data?.total_attention ?? 0;
    } catch {}
  },
});

Alpine.store('pomodoro', {
  open: false,
  active: false,
  paused: false,
  taskId: null,
  taskTitle: 'Focus',
  phase: 'work',       // 'work' | 'short_break' | 'long_break'
  remaining: 25 * 60,
  cycle: 0,            // completed work sessions
  _interval: null,
  _originalTitle: document.title,

  DURATIONS: { work: 25 * 60, short_break: 5 * 60, long_break: 15 * 60 },

  get timeStr() {
    const m = Math.floor(this.remaining / 60).toString().padStart(2, '0');
    const s = (this.remaining % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  },
  get phaseLabel() {
    return { work: 'Work', short_break: 'Short Break', long_break: 'Long Break' }[this.phase] ?? 'Work';
  },
  get pct() {
    return 1 - this.remaining / this.DURATIONS[this.phase];
  },

  start(taskId = null, taskTitle = 'Focus') {
    this.stop();
    this.taskId    = taskId;
    this.taskTitle = taskTitle;
    this.phase     = 'work';
    this.remaining = this.DURATIONS.work;
    this.paused    = false;
    this.active    = true;
    this.open      = true;
    this._run();
  },

  pause() {
    this.paused = true;
    clearInterval(this._interval);
    this._interval = null;
    document.title = this._originalTitle;
  },

  resume() {
    if (!this.active || !this.paused) return;
    this.paused = false;
    this._run();
  },

  skip() {
    clearInterval(this._interval);
    this._interval = null;
    this._nextPhase();
  },

  stop() {
    clearInterval(this._interval);
    this._interval = null;
    this.active    = false;
    this.paused    = false;
    this.open      = false;
    this.taskId    = null;
    this.taskTitle = 'Focus';
    this.phase     = 'work';
    this.remaining = this.DURATIONS.work;
    this.cycle     = 0;
    document.title = this._originalTitle;
  },

  _run() {
    this._interval = setInterval(() => {
      if (this.remaining <= 0) {
        clearInterval(this._interval);
        this._interval = null;
        this._notify();
        this._nextPhase();
        return;
      }
      this.remaining--;
      document.title = `${this.timeStr} [${this.phaseLabel}] — ChehreCRM`;
    }, 1000);
  },

  _nextPhase() {
    if (this.phase === 'work') {
      this.cycle++;
      this.phase     = this.cycle % 4 === 0 ? 'long_break' : 'short_break';
    } else {
      this.phase = 'work';
    }
    this.remaining = this.DURATIONS[this.phase];
    this.paused    = false;
    this._run();
  },

  _notify() {
    const msg = this.phase === 'work'
      ? `🍅 Pomodoro done! Time for a break.`
      : `⏰ Break over. Back to work!`;
    if (Notification.permission === 'granted') {
      new Notification('ChehreCRM Pomodoro', { body: msg, icon: '/icons/icon-192x192.png' });
    }
  },
});

Alpine.start();
requestAnimationFrame(() => window.lucide?.createIcons());

const i18n = Alpine.store('i18n');
document.documentElement.lang = i18n.locale;
document.documentElement.dir = i18n.dir;

createRouter(document.getElementById('app'));

// Init push after a short delay so auth store is settled
setTimeout(() => initPush(), 2000);

// Refresh attention count every 5 minutes when authenticated
setTimeout(() => {
  const auth = Alpine.store('auth');
  if (auth?.isAuthenticated && auth?.currentWorkspaceId) {
    Alpine.store('attention').refresh();
  }
}, 3000);
setInterval(() => {
  const auth = Alpine.store('auth');
  if (auth?.isAuthenticated && auth?.currentWorkspaceId) {
    Alpine.store('attention').refresh();
  }
}, 300000);

// PWA service worker registration with update banner
const updateSW = registerSW({
  onNeedRefresh() {
    const banner = document.createElement('div');
    banner.id = 'pwa-update-banner';
    banner.style.cssText = [
      'position:fixed', 'bottom:1rem', 'left:50%', 'transform:translateX(-50%)',
      'background:#1e40af', 'color:#fff', 'padding:0.75rem 1.25rem',
      'border-radius:0.5rem', 'font-size:0.875rem', 'display:flex',
      'align-items:center', 'gap:0.75rem', 'z-index:9999',
      'box-shadow:0 4px 12px rgba(0,0,0,.25)',
    ].join(';');
    banner.innerHTML = `
      <span>New version available</span>
      <button style="background:#fff;color:#1e40af;border:none;padding:0.25rem 0.75rem;border-radius:0.25rem;cursor:pointer;font-weight:600;"
              onclick="document.getElementById('pwa-update-banner').remove(); updateSW(true)">
        Update
      </button>
      <button style="background:transparent;border:none;color:#93c5fd;cursor:pointer;font-size:1rem;"
              onclick="document.getElementById('pwa-update-banner').remove()">&times;</button>
    `;
    document.body.appendChild(banner);
    window.updateSW = updateSW;
  },
  onOfflineReady() {},
});
